/* eslint-disable no-underscore-dangle */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import Error from '../../../Components/Error';
import PaymentsService from '../../../Services/PaymentsService';
import BookingService from '../../../Services/BookingService';
import { formatDateTime } from '../../../Utils/timezone';
import PaymentAllocationRows, {
  newAllocationRow,
  formatRowAmount,
  validateAllocationRows,
  rowsToLinesPayload,
} from './PaymentAllocationRows';
import { computeAllocatableLines } from './allocatableLines';

function formatMoney(amount, currency) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  const n = Number(amount);
  if (currency === 'HUF') return `${Math.round(n).toLocaleString('en-US')} Ft`;
  return `€${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Edit-allocation modal. Same row layout as LinkToBookingModal but
 * pre-populates rows from the existing payment's `lines[]` so the
 * operator can refine which order_line each portion hits without
 * starting from scratch. Submits via the edit-allocation endpoint.
 */
function EditAllocationModal({
  show, onHide, payment, onEdited,
}) {
  const rowIdCounter = useRef(0);
  const newRowId = () => {
    rowIdCounter.current += 1;
    return `row-${rowIdCounter.current}`;
  };

  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Seed from existing payment.lines on every (re-)open and kick off a
  // per-unique-booking fetch so the order-line dropdowns populate
  // without operator intervention.
  useEffect(() => {
    if (!show || !payment) return undefined;
    rowIdCounter.current = 0;
    setSubmitError('');

    const existingLines = Array.isArray(payment.lines) ? payment.lines : [];
    const baseId = payment.counterparty?.id;
    const cpKind = payment.counterparty?.kind;

    const initial = (() => {
      if (existingLines.length > 0) {
        return existingLines.map((line) => ({
          ...newAllocationRow(newRowId()),
          orderLineKey: line.order_line_key || '',
          amount: formatRowAmount(line.gross_amount, payment.currency),
          pendingBookingId: line.booking_id,
        }));
      }
      if (cpKind === 'booking' && baseId) {
        return [{
          ...newAllocationRow(newRowId()),
          amount: formatRowAmount(payment.gross_amount, payment.currency),
          pendingBookingId: baseId,
        }];
      }
      return [{
        ...newAllocationRow(newRowId()),
        amount: formatRowAmount(payment.gross_amount, payment.currency),
      }];
    })();
    setRows(initial);

    // One fetch per unique pending booking_id; reuse across rows.
    const uniqueIds = Array.from(new Set(
      initial.map((r) => r.pendingBookingId).filter(Boolean),
    ));
    let cancelled = false;
    uniqueIds.forEach((bookingId) => {
      (async () => {
        try {
          const full = await BookingService.getBookingById(bookingId);
          if (cancelled || !full) return;
          const lines = computeAllocatableLines(full);
          setRows((prev) => prev.map((r) => (r.pendingBookingId === bookingId
            ? {
              ...r,
              matchedBooking: full,
              allocatableLines: lines,
              orderLineKey: lines.some((l) => l._key === r.orderLineKey)
                ? r.orderLineKey
                : (lines[0]?._key || ''),
              loadingOrderLines: false,
            }
            : r)));
        } catch (err) {
          if (cancelled) return;
          setRows((prev) => prev.map((r) => (r.pendingBookingId === bookingId
            ? {
              ...r,
              loadingOrderLines: false,
              orderLinesError: err.response?.data?.message
                || err.message || 'Could not load order lines for this booking',
            }
            : r)));
        }
      })();
    });
    return () => { cancelled = true; };
  }, [show, payment?._id]);

  const expected = Number(payment?.gross_amount ?? 0);
  const validation = validateAllocationRows(rows, expected);
  const canSubmit = validation.canSubmit && !submitting;

  // "Nothing changed" guard so the operator doesn't fire a no-op API
  // call just by opening the modal and clicking Save.
  const isUnchanged = useMemo(() => {
    if (!payment) return true;
    const existing = Array.isArray(payment.lines) ? payment.lines : [];
    if (existing.length === 0) {
      return rows.length === 1
        && !rows[0].orderLineKey
        && Number(rows[0].amount) === Number(payment.gross_amount);
    }
    if (rows.length !== existing.length) return false;
    return rows.every((r, i) => existing[i].booking_id === r.matchedBooking?._id
      && (existing[i].order_line_key || '') === (r.orderLineKey || '')
      && Number(existing[i].gross_amount) === Number(r.amount));
  }, [payment, rows]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!payment || !canSubmit || isUnchanged) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const lines = rowsToLinesPayload(rows);
      const updated = await PaymentsService.editPaymentAllocation(payment._id, lines);
      if (onEdited) onEdited(updated);
    } catch (err) {
      setSubmitError(err.response?.data?.message || err.message || 'Failed to edit allocation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    if (onHide) onHide();
  };

  return (
    <Modal show={show} onHide={handleCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-pencil me-2" />
          Edit Payment Allocation
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {payment && (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="text-muted small mb-1">Editing allocation for</div>
            <div className="fw-bold">
              {formatMoney(payment.gross_amount, payment.currency)}
              {' '}
              <span className="text-muted small">
                (
                {payment.currency || '—'}
                ,
                {' '}
                {payment.direction === 'in' ? 'inbound' : 'outbound'}
                )
              </span>
            </div>
            <div className="small text-muted">
              {payment.occurred_at ? formatDateTime(payment.occurred_at) : '—'}
            </div>
            {payment.external_refs?.stripe_charge_id && (
              <div className="small mt-1">
                <code>{payment.external_refs.stripe_charge_id}</code>
              </div>
            )}
          </div>
        )}

        <Error error={submitError} setError={setSubmitError} />

        <Form onSubmit={handleSubmit}>
          <PaymentAllocationRows
            rows={rows}
            onChange={setRows}
            expectedTotal={expected}
            currency={payment?.currency || 'EUR'}
            sideMode="customer"
            disabled={submitting}
            testIdPrefix="ea"
            // Exclude THIS payment from the "already-paid-per-line"
            // decoration the picker uses — otherwise the row that the
            // operator is currently re-allocating would count itself
            // and a line would look fully-paid when it isn't.
            excludePaymentId={payment?._id}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || isUnchanged}
          title={isUnchanged ? 'No changes to save' : undefined}
        >
          {submitting ? 'Saving…' : 'Save allocation'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditAllocationModal;
